import clsx from "clsx"
import { forwardRef, useCallback, useEffect, useState } from "react"

import { isAudio, isPDF } from "@utils/fileUtils"

import FileDisplay from "@organisms/FileDisplay/FileDisplay"
import FileCarousel from "@organisms/SidePanel/SidePanelFilePreview/FileCarousel/FileCarousel"

import styles from "./SidePanelFilePreview.module.scss"
import type { SidePanelFilePreviewProps } from "./SidePanelFilePreview.types"

function SidePanelFilePreview(props: SidePanelFilePreviewProps, ref: React.Ref<HTMLDivElement>) {
    const { files, initialIndex = 0, isNavigationDisabled = false, onIndexChange, className, ...rest } = props

    const [currentIndex, setCurrentIndex] = useState(initialIndex)

    // Clamp index when files array shrinks (e.g., after deletion)
    useEffect(() => {
        if (currentIndex >= files.length && files.length > 0) {
            setCurrentIndex(files.length - 1)
        }
    }, [files.length, currentIndex])

    const currentFile = files[currentIndex]
    const hasMultipleFiles = files.length > 1
    const mimeType = currentFile?.upload_mimetype ?? ""
    const isPdfOrAudio = isPDF(mimeType) || isAudio(mimeType)

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prev) => Math.max(0, prev - 1))
    }, [])

    const goToNext = useCallback(() => {
        setCurrentIndex((prev) => Math.min(files.length - 1, prev + 1))
    }, [files.length])

    const handleIndexChange = useCallback((index: number) => {
        setCurrentIndex(index)
    }, [])

    // Keyboard navigation
    useEffect(() => {
        if (isNavigationDisabled) {
            return
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement

            // Skip navigation when focus is on an input element
            if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
                return
            }

            if (event.key === "ArrowLeft") {
                event.preventDefault()
                goToPrevious()
            } else if (event.key === "ArrowRight") {
                event.preventDefault()
                goToNext()
            }
        }

        window.addEventListener("keydown", handleKeyDown)
        return () => window.removeEventListener("keydown", handleKeyDown)
    }, [isNavigationDisabled, goToPrevious, goToNext])

    // Notify parent of index changes
    useEffect(() => {
        onIndexChange?.(currentIndex)
    }, [currentIndex, onIndexChange])

    if (!currentFile) {
        return null
    }

    return (
        <div ref={ref} className={clsx("dark-theme", styles.base, className)} {...rest}>
            <div
                className={clsx(styles.fileDisplayContainer, {
                    [styles.isPdfOrAudio]: isPdfOrAudio,
                })}
            >
                <FileDisplay attachment={currentFile} />
            </div>

            {hasMultipleFiles && (
                <FileCarousel
                    files={files}
                    currentIndex={currentIndex}
                    onSelect={(index) => handleIndexChange(index)}
                />
            )}
        </div>
    )
}

export default forwardRef(SidePanelFilePreview)
